/**
 * shipmentReceiptUtils.ts
 *
 * Pure helpers for the vendor receipt form (Verify Receipt by SKU): the editable draft,
 * live variance detection, submit validation and the request payload.
 */
import type {
  ReceiptAction,
  ReceiptCondition,
  ReceiptRequest,
  ReceiptSku,
  SiteOutcome,
  UnitDisposition,
} from "../../../api/shipmentApi";
import { sanitizeFreeText } from "./sanitizeFreeText";

export const NOTES_MIN_LENGTH = 15;
export const NOTES_MAX_LENGTH = 2000;

export const RECEIPT_MESSAGES = {
  notesRequired:
    "Describe the variance in detail before flagging it. State which SKU, how many units, and the condition they arrived in.",
  notesPlaceholder:
    "Describe exactly what is wrong: which SKU, how many units, and what condition they arrived in.",
  notesGuidance:
    "Unexpected or unapproved items, and A720x assets found in a Peak shipment, must be separated and escalated for PepsiCo direction.",
  noVariance: "No variance. Counts match the shipment record to the unit level.",
  varianceStatement:
    "Confirming with a variance flags the shipment instead of reconciling it. Warehouse Support and the submitting site are both notified.",
  confirmFailedRetry: "Confirmation failed. Your count is saved. Retry when you have a connection.",
  quantityInvalid: "Enter the received quantity as a whole number.",
  unverified: (count: number) => `Verify every SKU before confirming receipt. ${count} SKU(s) still unverified.`,
} as const;

export const DISPOSITION_OPTIONS: ReadonlyArray<{ value: UnitDisposition; label: string }> = [
  { value: "RECEIVED", label: "Received" },
  { value: "DAMAGED", label: "Damaged in transit" },
  { value: "NOT_IN_SHIPMENT", label: "Not in shipment" },
];

export const CONDITION_OPTIONS: ReadonlyArray<ReceiptCondition> = ["Good", "Damaged", "Incomplete"];

export const SITE_OUTCOME_LABELS: Record<SiteOutcome, string> = {
  UNITS_LOCATED_RESHIPPED: "Units Located and Reshipped",
  UNITS_CANNOT_BE_LOCATED: "Units Cannot Be Located",
};

// ---------------------------------------------------------------------------
// Draft
// ---------------------------------------------------------------------------

export interface ReceiptDraft {
  /** SKU -> "SKU verified" checkbox. */
  verified: Record<string, boolean>;
  /** Serialized unit lineId -> disposition. */
  dispositions: Record<string, UnitDisposition>;
  /** Quantity-capture SKU -> received quantity as typed. */
  quantities: Record<string, string>;
  condition: ReceiptCondition;
  notes: string;
  unexpectedSerials: string[];
  /** One id per action, reused on retries so a confirmation never double-posts. */
  requestIds: Partial<Record<ReceiptAction, string>>;
}

export const buildInitialDraft = (skus: ReceiptSku[]): ReceiptDraft => {
  const verified: Record<string, boolean> = {};
  const dispositions: Record<string, UnitDisposition> = {};
  const quantities: Record<string, string> = {};
  skus.forEach((s) => {
    verified[s.sku] = false;
    if (s.captureMode === "SERIAL") {
      (s.units ?? []).forEach((u) => {
        dispositions[String(u.lineId)] = u.disposition ?? "RECEIVED";
      });
    } else {
      quantities[s.sku] = String(s.receivedQuantity ?? s.expectedQuantity);
    }
  });
  return { verified, dispositions, quantities, condition: "Good", notes: "", unexpectedSerials: [], requestIds: {} };
};

/** Overlays a stored draft onto the current shipment structure, ignoring anything that no longer exists. */
export const reconcileDraft = (skus: ReceiptSku[], stored: Partial<ReceiptDraft> | null | undefined): ReceiptDraft => {
  const base = buildInitialDraft(skus);
  if (!stored) return base;
  Object.keys(base.verified).forEach((k) => {
    if (typeof stored.verified?.[k] === "boolean") base.verified[k] = stored.verified[k];
  });
  Object.keys(base.dispositions).forEach((k) => {
    const v = stored.dispositions?.[k];
    if (v === "RECEIVED" || v === "DAMAGED" || v === "NOT_IN_SHIPMENT") base.dispositions[k] = v;
  });
  Object.keys(base.quantities).forEach((k) => {
    if (typeof stored.quantities?.[k] === "string") base.quantities[k] = stored.quantities[k];
  });
  if (stored.condition && CONDITION_OPTIONS.includes(stored.condition)) base.condition = stored.condition;
  if (typeof stored.notes === "string") base.notes = stored.notes.slice(0, NOTES_MAX_LENGTH);
  if (Array.isArray(stored.unexpectedSerials)) {
    base.unexpectedSerials = stored.unexpectedSerials.filter((s) => typeof s === "string" && s.trim());
  }
  if (stored.requestIds && typeof stored.requestIds === "object") base.requestIds = { ...stored.requestIds };
  return base;
};

// ---------------------------------------------------------------------------
// Variance
// ---------------------------------------------------------------------------

export type VarianceType = "NOT_IN_SHIPMENT" | "DAMAGED" | "QUANTITY" | "UNEXPECTED";

export interface VarianceItem {
  key: string;
  type: VarianceType;
  /** Absent for unexpected items, which belong to no SKU on the shipment. */
  sku?: string;
  detail: string;
}

/** A whole number >= 0, or null when the text is not one. */
export const parseQuantity = (text: string | undefined): number | null => {
  const t = (text ?? "").trim();
  if (!/^\d{1,6}$/.test(t)) return null;
  return parseInt(t, 10);
};

export const normalizeSerial = (serial: string): string => serial.trim().toUpperCase();

/** One entry per affected SKU and variance type, matching how the backend counts variances. */
export const computeVariances = (skus: ReceiptSku[], draft: ReceiptDraft): VarianceItem[] => {
  const items: VarianceItem[] = [];
  skus.forEach((s) => {
    if (s.captureMode === "SERIAL") {
      const damaged: string[] = [];
      const missing: string[] = [];
      (s.units ?? []).forEach((u) => {
        const d = draft.dispositions[String(u.lineId)] ?? "RECEIVED";
        if (d === "DAMAGED") damaged.push(u.serialNumber);
        if (d === "NOT_IN_SHIPMENT") missing.push(u.serialNumber);
      });
      if (damaged.length) {
        items.push({
          key: `${s.sku}:DAMAGED`,
          type: "DAMAGED",
          sku: s.sku,
          detail: `${damaged.length} unit(s) damaged in transit: ${damaged.join(", ")}`,
        });
      }
      if (missing.length) {
        items.push({
          key: `${s.sku}:NOT_IN_SHIPMENT`,
          type: "NOT_IN_SHIPMENT",
          sku: s.sku,
          detail: `${missing.length} unit(s) not in shipment: ${missing.join(", ")}`,
        });
      }
    } else {
      const received = parseQuantity(draft.quantities[s.sku]);
      if (received !== null && received !== s.expectedQuantity) {
        const diff = Math.abs(s.expectedQuantity - received);
        items.push({
          key: `${s.sku}:QUANTITY`,
          type: "QUANTITY",
          sku: s.sku,
          detail: `received ${received} of ${s.expectedQuantity} expected (${diff} ${received < s.expectedQuantity ? "short" : "over"})`,
        });
      }
    }
  });
  if (draft.unexpectedSerials.length) {
    items.push({
      key: "UNEXPECTED",
      type: "UNEXPECTED",
      detail: `${draft.unexpectedSerials.length} unexpected item(s) not on this shipment: ${draft.unexpectedSerials.join(", ")}`,
    });
  }
  return items;
};

/** Variances as recorded on a submitted receipt (dispositions and quantities come from the server). */
export const computeRecordedVariances = (skus: ReceiptSku[], unexpectedSerials?: string[] | null): VarianceItem[] =>
  computeVariances(skus, { ...buildInitialDraft(skus), unexpectedSerials: unexpectedSerials ?? [] });

export const countUnverified = (skus: ReceiptSku[], draft: ReceiptDraft): number =>
  skus.filter((s) => !draft.verified[s.sku]).length;

/** Finds a shipped serialized unit by a scanned or typed serial (case-insensitive). */
export const findUnitBySerial = (skus: ReceiptSku[], serial: string): { sku: string; lineId: number; serialNumber: string } | null => {
  const wanted = normalizeSerial(serial);
  if (!wanted) return null;
  for (const s of skus) {
    for (const u of s.units ?? []) {
      if (normalizeSerial(u.serialNumber) === wanted) return { sku: s.sku, lineId: u.lineId, serialNumber: u.serialNumber };
    }
  }
  return null;
};

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

export const getNotesError = (notes: string): string | null =>
  sanitizeFreeText(notes, NOTES_MAX_LENGTH).length < NOTES_MIN_LENGTH ? RECEIPT_MESSAGES.notesRequired : null;

export type SubmitValidation =
  | { ok: true }
  | { ok: false; field: "verify" | "quantity" | "notes"; message: string; sku?: string };

/** Notes are required for a flag and for any confirmation that carries a variance. */
export const validateSubmit = (action: ReceiptAction, skus: ReceiptSku[], draft: ReceiptDraft): SubmitValidation => {
  if (action === "FLAG") {
    const err = getNotesError(draft.notes);
    return err ? { ok: false, field: "notes", message: err } : { ok: true };
  }
  const unverified = countUnverified(skus, draft);
  if (unverified > 0) return { ok: false, field: "verify", message: RECEIPT_MESSAGES.unverified(unverified) };
  const badQty = skus.find((s) => s.captureMode === "QUANTITY" && parseQuantity(draft.quantities[s.sku]) === null);
  if (badQty) return { ok: false, field: "quantity", message: RECEIPT_MESSAGES.quantityInvalid, sku: badQty.sku };
  if (computeVariances(skus, draft).length > 0) {
    const err = getNotesError(draft.notes);
    if (err) return { ok: false, field: "notes", message: err };
  }
  return { ok: true };
};

// ---------------------------------------------------------------------------
// Request
// ---------------------------------------------------------------------------

export const newClientRequestId = (): string => {
  const c = (globalThis as any).crypto;
  if (c?.randomUUID) return c.randomUUID();
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (ch) => {
    const r = Math.floor(Math.random() * 16);
    return (ch === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
};

export const buildReceiptRequest = (
  action: ReceiptAction,
  skus: ReceiptSku[],
  draft: ReceiptDraft,
  clientRequestId: string
): ReceiptRequest => {
  const notes = sanitizeFreeText(draft.notes, NOTES_MAX_LENGTH) || null;
  if (action === "FLAG") {
    return { action, clientRequestId, condition: draft.condition, notes };
  }
  return {
    action,
    clientRequestId,
    condition: draft.condition,
    notes,
    skus: skus.map((s) =>
      s.captureMode === "SERIAL"
        ? {
            sku: s.sku,
            verified: !!draft.verified[s.sku],
            units: (s.units ?? []).map((u) => ({
              serial: u.serialNumber,
              disposition: draft.dispositions[String(u.lineId)] ?? "RECEIVED",
            })),
          }
        : {
            sku: s.sku,
            verified: !!draft.verified[s.sku],
            receivedQuantity: parseQuantity(draft.quantities[s.sku]) ?? s.expectedQuantity,
          }
    ),
    unexpectedSerials: draft.unexpectedSerials,
  };
};

// ---------------------------------------------------------------------------
// Display helpers
// ---------------------------------------------------------------------------

/** "2026-08-26" -> "08/26/2026" without a timezone shift. */
export const formatIsoDate = (iso?: string | null): string => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso ?? "");
  return m ? `${m[2]}/${m[3]}/${m[1]}` : "-";
};

/** Short vendor name for button labels: "Peak Technologies" -> "Peak". */
export const shortVendorName = (vendorName?: string | null): string => {
  const first = (vendorName ?? "").trim().split(/\s+/)[0];
  return first || "Vendor";
};

export const countUnits = (skus: ReceiptSku[] | null | undefined): number =>
  (skus ?? []).reduce((total, s) => total + (s.captureMode === "SERIAL" ? (s.units ?? []).length : s.expectedQuantity), 0);
